import React, { useRef, useState } from 'react'
import { Row, Col, Form, Flex } from 'antd'
import { CardHeader, SectionCard } from '@/shared/ui/layout/styled'
import { Icon } from '@/shared/ui/Icon/Icon'
import { MediumInput } from '@/shared/ui/components/Input'
import { Typography } from 'antd'
import { FormItem, HelpText } from '@/shared/ui/forms/FormItem/FormItem'
import { Button } from '@/shared/ui'
import { InfoAlert } from '@/shared/ui/components/Alert'
import { SystemModal } from './styled'
import { useBodyScrollLock } from '@/app/hooks/useBodyScrollLock'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'

const { Title, Text, Link } = Typography

export interface TokenCardProps {
  systemToken: string
  onTokenChange: (token: string) => void
  systemUrl: string
  onUrlChange: (url: string) => void
  isTokenValid?: boolean
  isValidatingToken?: boolean
  tokenAccount?: string | null
  systemTitle?: string | null
}

export const TokenCard: React.FC<TokenCardProps> = ({
  systemToken,
  onTokenChange,
  systemUrl,
  onUrlChange,
  isTokenValid = false,
  isValidatingToken = false,
  tokenAccount = '',
  systemTitle,
}) => {
  const [isTokenModalVisible, setIsTokenModalVisible] = useState(false)
  const [iframeKey, setIframeKey] = useState(0)
  const { isSm, isXs } = useBreakpoint()
  const isMobile = isXs || isSm
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTokenChange(e.target.value)
  }

  const getWialonAuthUrl = () => {
    let authBaseUrl = 'https://hosting.wialon.com'

    try {
      const urlObj = new URL(systemUrl)
      const hostname = urlObj.hostname

      if (hostname.endsWith('.wialon.com')) {
        authBaseUrl = 'https://hosting.wialon.com'
      } else {
        authBaseUrl = `${urlObj.protocol}//${urlObj.host}`
      }
    } catch (error) {
      console.error('Invalid URL, using default auth server')
    }

    const params = new URLSearchParams({
      client_id: 'Glonasssoft_Transfer',
      access_type: '-1',
      activation_time: '0',
      duration: '0',
      lang: 'ru',
      flags: '0',
      redirect_uri: `${window.location.origin}/transfer`,
    })

    return `${authBaseUrl}/login.html?${params.toString()}`
  }

  const handleIframeLoad = async (
    e: React.SyntheticEvent<HTMLIFrameElement>
  ) => {
    const iframe = e.target as HTMLIFrameElement
    try {
      const iframeUrl = iframe.contentWindow?.location.href

      if (iframeUrl && iframeUrl.includes('access_token=')) {
        const urlParams = new URLSearchParams(iframeUrl.split('?')[1])
        const token = urlParams.get('access_token')

        if (token) {
          onTokenChange(token)
          setIsTokenModalVisible(false)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleGetToken = () => {
    setIsTokenModalVisible(true)
    setIframeKey((prev) => prev + 1)
  }

  const handleCloseModal = () => {
    setIsTokenModalVisible(false)
    setIframeKey((prev) => prev + 1)
  }

  useBodyScrollLock(isTokenModalVisible)

  return (
    <SectionCard>
      <CardHeader gap={8} align="center">
        <Icon name="token" size={32} />
        <Title level={2}>Токены доступа</Title>
      </CardHeader>
      <Form>
        <Row gutter={[32, 32]}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <FormItem
              validateStatus={
                isValidatingToken ? 'validating' : isTokenValid ? 'success' : ''
              }
              help={
                isValidatingToken ? (
                  <Flex gap={4} align="center">
                    <Icon name="spinner" size={16} spin />
                    <Text type="secondary">Проверка токена...</Text>
                  </Flex>
                ) : isTokenValid ? (
                  <Flex gap={4} align="center">
                    <Icon name="token_valid" size={12} />
                    <Text type="success" style={{ fontSize: '12px' }}>
                      Токен валиден. Учетная запись: {tokenAccount}
                    </Text>
                  </Flex>
                ) : systemToken ? (
                  <Flex gap={4} align="center">
                    <Icon name="input_error" size={12} />
                    <Text type="danger">Токен должен содержать 72 символа</Text>
                  </Flex>
                ) : null
              }
            >
              <MediumInput
                label={`${systemTitle} Token`}
                id={systemTitle?.toLowerCase() + '-token'}
                value={systemToken}
                suffix={
                  isValidatingToken ? (
                    <Icon name="spinner" size={16} spin />
                  ) : (
                    <Icon name="token_key" size={16} />
                  )
                }
                onChange={handleTokenChange}
                disabled={isValidatingToken}
                placeholder={`Введите токен ${systemTitle} (72 символа)`}
              />
            </FormItem>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <FormItem
              help={
                <HelpText type="secondary">
                  {`URL адрес ${systemTitle} сервера для подключения`}
                </HelpText>
              }
            >
              <MediumInput
                value={systemUrl}
                label={`${systemTitle} URL`}
                suffix={<Icon name="token_url" size={16} />}
                onChange={(e) => onUrlChange(e.target.value)}
              />
            </FormItem>
          </Col>
        </Row>
      </Form>
      <Flex vertical gap={16}>
        <Button
          style={{ alignSelf: 'start' }}
          type="primary"
          icon={<Icon name="get_token" size={16} />}
          onClick={handleGetToken}
        >
          Получить токен
        </Button>
        <InfoAlert
          title={
            <>
              {`Как получить токен ${systemTitle}: `}
              <Link
                href="https://help.wialon.com/ru/api/expert-articles/faq/frequently-asked-questions#max-token"
                target="_blank"
              >
                инструкция
              </Link>
            </>
          }
        />
      </Flex>

      <SystemModal
        title={`Авторизация ${systemTitle} для получения токена`}
        open={isTokenModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={930}
        closeIcon={
          <Icon
            name={isMobile ? 'closed_modal_phone' : 'closed_modal'}
            size={16}
          />
        }
        centered
      >
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={getWialonAuthUrl()}
          width="100%"
          height="485"
          frameBorder="0"
          onLoad={handleIframeLoad}
          style={{ border: 'none', borderRadius: '8px' }}
        />
      </SystemModal>
    </SectionCard>
  )
}
